/**
 * useCmdDiscoverSignals — fetches the safe-surface CMD signals for a set
 * of currently-visible creator IDs.
 *
 * Per the audit contract (/app/docs/CMD_PIPELINE_HEALTH_AUDIT.md §4 + §9):
 *   • Only the safe surfaces are consumed (tags, latent coords, similar
 *     pairs, ui_guard).
 *   • The endpoint is feature-flagged on the backend by
 *     CMD_DISCOVER_ENABLED; when off, the hook returns `{enabled: false}`
 *     and the consuming UI should not render CMD chrome.
 *   • The hook NEVER reads `projected_*_lift` / `revenue_*` fields — the
 *     backend does not emit them, and a defensive lint here would catch
 *     it if they ever appeared (we just don't pass them through).
 *
 * Debouncing: the visible-creator set changes on every filter keystroke,
 * so we debounce 350ms to avoid hammering the endpoint mid-typing.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const EMPTY_SIGNALS = {
  enabled: false,
  loading: false,
  error: null,
  method: null,
  fallback: false,
  explainedVar: 0,
  patchCount: 0,
  tags: {},                // {creator_id: {patch_id, patch_role, patch_role_label, is_bridge, is_xplat_amplifier}}
  latentCoords: {},        // {creator_id: [x, y]}
  couplingMatrix: {},      // {patch_id: {patch_id_neighbor: float}}
  similarPairs: [],        // [{creator_a, creator_b, audience_overlap_pct}]
  uiGuard: { hide_scatter: true, reason: 'feature_disabled' },
};

export function useCmdDiscoverSignals(creatorIds) {
  // Stable signature so we don't refetch when the array reference changes
  // but the contents are identical.
  const signature = useMemo(
    () => (creatorIds || []).slice(0, 300).join(','),
    [creatorIds],
  );

  const [state, setState] = useState(EMPTY_SIGNALS);
  const lastSignatureRef = useRef(null);

  useEffect(() => {
    if (!signature) {
      setState(EMPTY_SIGNALS);
      return undefined;
    }
    if (lastSignatureRef.current === signature) return undefined;

    let cancelled = false;
    const timer = setTimeout(async () => {
      lastSignatureRef.current = signature;
      setState((prev) => ({ ...prev, loading: true, error: null }));
      try {
        const ids = signature.split(',').filter(Boolean);
        const { data } = await axios.post(
          `${API}/api/brand/discover/cmd-signals`,
          { creator_ids: ids },
          { withCredentials: true },
        );
        if (cancelled) return;
        setState({
          enabled:         Boolean(data?.enabled),
          loading:         false,
          error:           null,
          method:          data?.method || null,
          fallback:        Boolean(data?.fallback),
          explainedVar:    Number(data?.explained_var || 0),
          patchCount:      Number(data?.patch_count || 0),
          tags:            data?.tags || {},
          latentCoords:    data?.latent_coords || {},
          couplingMatrix:  data?.coupling_matrix || {},
          similarPairs:    data?.similar_pairs || [],
          uiGuard:         data?.ui_guard || { hide_scatter: true, reason: null },
        });
      } catch (err) {
        if (cancelled) return;
        if (process.env.NODE_ENV !== 'production') {
          // Non-fatal — the page renders fine without CMD signals.
          console.warn('useCmdDiscoverSignals:', err?.message || err);
        }
        setState({ ...EMPTY_SIGNALS, error: err?.message || 'Failed to load CMD signals' });
      }
    }, 350);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [signature]);

  return state;
}
